import { formatUnits } from 'viem';

import { DASH, MZUSD_DECIMALS, NA_VALUE } from '@/app/shared/constants/common';

export const shortenAddress = (address: string, startChars = 6, endChars = 5): string =>
  `${address.slice(0, startChars)}...${address.slice(-endChars)}`;

export const parseNumber = (raw?: string): number | null => {
  if (raw == null || raw === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
};

export const toBigIntString = (value: string | number | undefined): string | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return value;
  return value.toLocaleString('fullwide', { useGrouping: false });
};

export const toBigInt = (value?: string | number | null, fallback = 0n): bigint => {
  const raw = typeof value === 'number' ? toBigIntString(value) : value;
  if (raw == null || raw === '' || !/^-?\d+$/.test(raw)) return fallback;
  return BigInt(raw);
};

export const formatUsd = (value?: string | number): string => {
  if (value == null || value === '') return DASH;
  const num = Number(value);
  if (Number.isNaN(num)) return DASH;
  return `$${formatNumber(num, 2, 2)}`;
};

export const formatApy = (value?: string): string => {
  if (value == null || value === '') return DASH;
  const num = Number(value);
  if (Number.isNaN(num)) return DASH;
  return `${formatNumber(num * 100, 2, 2)}%`;
};

export const formatTokenAmount = (value?: string | number, decimals = MZUSD_DECIMALS): string => {
  const raw = typeof value === 'number' ? toBigIntString(value) : value;
  if (!raw) return DASH;
  try {
    return formatNumber(formatUnits(BigInt(raw), decimals), 2, 2);
  } catch {
    const num = Number(value);
    return Number.isNaN(num) ? DASH : formatNumber(num, 2, 2);
  }
};

export const formatFee = (value?: string | number | null): string => {
  if (value == null || value === '') return 'None';

  const raw = typeof value === 'number' ? toBigIntString(value) : value;
  if (!raw || raw === '0') return 'None';

  try {
    const human = Number(formatUnits(BigInt(raw), MZUSD_DECIMALS));
    if (!Number.isFinite(human) || human === 0) return 'None';
    return `$${formatNumber(human, 6, 6)}`;
  } catch {
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    if (num === 0) return 'None';
    return `$${formatNumber(num, 6, 6)}`;
  }
};

export const formatHistoryDate = (timestamp?: string): string => {
  if (!timestamp) return DASH;
  const date = new Date(Number(timestamp) * 1000);
  if (Number.isNaN(date.getTime())) return DASH;

  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });
};

export const formatAxisDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatAxisCompact = (
  value: number,
  prefix = '',
  suffix = '',
  maxFractionDigits = 2,
  minFractionDigits = 2,
): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `${prefix}${formatNumber(value / 1_000_000_000, maxFractionDigits, minFractionDigits)}b${suffix}`;
  }
  if (abs >= 1_000_000) {
    return `${prefix}${formatNumber(value / 1_000_000, maxFractionDigits, minFractionDigits)}m${suffix}`;
  }
  if (abs >= 1_000) {
    return `${prefix}${formatNumber(value / 1_000, maxFractionDigits, minFractionDigits)}k${suffix}`;
  }
  return `${prefix}${formatNumber(value, maxFractionDigits, minFractionDigits)}${suffix}`;
};

export const formatUnlockDuration = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return 'Instant';

  const epochs = Math.floor(totalSeconds / (7 * 24 * 60 * 60));
  if (epochs >= 1 && totalSeconds % (7 * 24 * 60 * 60) === 0) {
    return epochs === 1 ? '1 epoch' : `${epochs} epochs`;
  }

  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  if (days >= 1 && totalSeconds % (24 * 60 * 60) === 0) {
    return days === 1 ? '1 day' : `${days} days`;
  }

  const hours = Math.floor(totalSeconds / (60 * 60));
  if (hours >= 1 && totalSeconds % (60 * 60) === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  if (minutes >= 1) {
    return minutes === 1 ? '1 min' : `${minutes} min`;
  }

  return `${totalSeconds} sec`;
};

/** Format an absolute availability timestamp (unix seconds) in the user's local timezone. */
export const formatEstimatedAvailability = (availabilityUnixSeconds: number): string => {
  const date = new Date(availabilityUnixSeconds * 1000);
  const day = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const timeZone =
    new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? '';

  return timeZone ? `${day}, ${time} ${timeZone}` : `${day}, ${time}`;
};

/**
 * Claimable-at timestamp from vault/tranche reads:
 * nextDuration = P != 0 ? P : epochDuration
 * epochEnd     = epochStart + epochDuration
 * claimableAt  = epochEnd + D * nextDuration
 */
export const calcClaimableAtUnix = ({
  epochStartUnix,
  epochDurationSeconds,
  pendingEpochDurationSeconds,
  unstakeDelayEpochs,
}: {
  epochStartUnix: number;
  epochDurationSeconds: number;
  pendingEpochDurationSeconds: number;
  unstakeDelayEpochs: number;
}): { claimableAtUnix: number; unlockPeriodSeconds: number; nextDurationSeconds: number } => {
  const nextDurationSeconds =
    pendingEpochDurationSeconds !== 0 ? pendingEpochDurationSeconds : epochDurationSeconds;
  const unlockPeriodSeconds = Math.max(0, unstakeDelayEpochs) * Math.max(0, nextDurationSeconds);
  const epochEndUnix = epochStartUnix + epochDurationSeconds;
  const claimableAtUnix = epochEndUnix + unlockPeriodSeconds;

  return { claimableAtUnix, unlockPeriodSeconds, nextDurationSeconds };
};

export const calcEstimatedAvailabilityUnix = (
  epochStartUnix: number,
  epochDurationSeconds: number,
  unlockPeriodSeconds: number,
): number => {
  const epochEndUnix = epochStartUnix + epochDurationSeconds;
  return epochEndUnix + Math.max(0, unlockPeriodSeconds);
};

export const formatUnlockEta = (unlockEta?: string): string => {
  if (!unlockEta) return DASH;
  const unlockMs = Number(unlockEta) * 1000;
  if (Number.isNaN(unlockMs)) return DASH;

  const diffMs = unlockMs - Date.now();
  if (diffMs <= 0) return 'Ready';

  const totalMinutes = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
};

export const formatPointsPerDay = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return formatNumber(num, 2, 0);
};

export const formatPoints = (raw: string, decimals = 18) => {
  const formatted = formatUnits(BigInt(raw), decimals);
  return formatNumber(formatted);
};

export const truncateAmountToDecimals = (value: string, maxDecimals: number): string => {
  if (!value.includes('.')) return value;
  const [integerPart, fractionPart = ''] = value.split('.');
  if (fractionPart.length <= maxDecimals) return value;
  if (maxDecimals <= 0) return integerPart;
  return `${integerPart}.${fractionPart.slice(0, maxDecimals)}`;
};

export const formatNumberInputValue = (
  value: string,
  previousValue = '',
  maxDecimals?: number,
): string => {
  if (value.includes(',')) return previousValue;

  let _value = value.replace(/[^0-9.]/g, '');
  const parts = _value.split('.');

  // If there are more than one decimal point, join all parts after the first one
  if (parts.length > 2) {
    _value = parts[0] + '.' + parts.slice(1).join('');
  }

  // Prevent input from being just a decimal point
  if (_value === '.') {
    _value = '';
  }

  // Prevent multiple leading zeros before the decimal point
  if (_value.startsWith('0') && !_value.startsWith('0.') && _value.length > 1) {
    _value = '0';
  }

  if (maxDecimals !== undefined) {
    _value = truncateAmountToDecimals(_value, maxDecimals);
  }

  return _value;
};

export const isValidAmountInput = (value: string): boolean => {
  if (!value || value.endsWith('.')) return false;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
};

export const formatNumber = (
  num: number | string | undefined | null,
  maxFractionDigits = 2,
  minFractionDigits = 2,
): string => {
  const _num = parseFloat(String(num));
  if (isNaN(_num) || _num === 0) return maxFractionDigits === 0 ? '0' : `0.${'0'.repeat(maxFractionDigits)}`;

  const threshold = maxFractionDigits === 0 ? 1 : 1 / 10 ** maxFractionDigits;
  if (_num < threshold && _num > 0 && maxFractionDigits > 0) {
    return `<0.${'0'.repeat(maxFractionDigits - 1)}1`;
  }

  const numStr: string = _num.toString();
  const [integerPart, decimalPart = ''] = numStr.split('.');

  let truncatedDecimal: string = decimalPart.slice(0, maxFractionDigits);

  if (truncatedDecimal.length < minFractionDigits) {
    truncatedDecimal = truncatedDecimal.padEnd(minFractionDigits, '0');
  }

  const truncatedNum: number = truncatedDecimal
    ? parseFloat(`${integerPart}.${truncatedDecimal}`)
    : parseFloat(integerPart);

  return truncatedNum.toLocaleString('en-US', {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  });
};

export const shortNumber = (number: number, maxFractionDigits: number = 2, minFractionDigits: number = 1): string => {
  if (isNaN(number)) return maxFractionDigits === 0 ? '0' : `0.${'0'.repeat(maxFractionDigits)}`;
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits,
  }).format(number);
};

export const formatRenderValue = (
  value: string | undefined,
  formatter: (v: string, maxFractionDigits?: number, minFractionDigits?: number) => string,
  decimals: number,
) => (value ? formatter(formatUnits(BigInt(value), decimals)) : NA_VALUE);

// MintForm timer formatter
export const formatTimer = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

//  11:43 AM, Nov 6, 2025
export const formatDateTime = (timestamp: number | string | undefined | null): string => {
  if (!timestamp || timestamp === 0 || timestamp === '0') return NA_VALUE;

  const date = new Date(Number(timestamp) * 1000);

  if (isNaN(date.getTime())) return NA_VALUE;

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${timeStr}, ${dateStr}`;
};
